import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Star } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { adminApi, apiError } from "../api";
import { resolveImg } from "../../context/SiteContext";
import ImagePicker from "../components/ImagePicker";
import ListEditor from "../components/ListEditor";

const EMPTY = { title: "", days: 3, nights: 2, price: 0, cover: "", places: [], inclusions: [], exclusions: [], itinerary: [], badge: "", featured: false };

export default function PackagesManager() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => adminApi.get("/admin/packages").then(({ data }) => setItems(data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const set = (k) => (v) => setEditing((s) => ({ ...s, [k]: v?.target ? v.target.value : v }));

  const save = async () => {
    if (!editing.title) return toast.error("Title is required");
    setSaving(true);
    const payload = { ...editing, days: Number(editing.days), nights: Number(editing.nights), price: Number(editing.price) || 0 };
    try {
      if (isNew) await adminApi.post("/admin/packages", payload);
      else await adminApi.put(`/admin/packages/${editing.id}`, payload);
      toast.success("Saved — the Packages page is updated");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id, title) => {
    if (!window.confirm(`Delete "${title}"?`)) return;
    try {
      await adminApi.delete(`/admin/packages/${id}`);
      toast.success("Package deleted");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="admin-packages-page">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold">Tour Packages</h1>
          <p className="text-sm text-muted-foreground">{items.length} packages · featured ones show on the home page</p>
        </div>
        <Button onClick={() => { setEditing({ ...EMPTY }); setIsNew(true); }} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="add-package-btn">
          <Plus className="h-4 w-4 mr-1.5" /> Add package
        </Button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((p) => (
          <div key={p.id} className="rounded-xl border bg-white dark:bg-slate-900 overflow-hidden" data-testid={`pkg-row-${p.id}`}>
            <div className="h-32 bg-muted overflow-hidden relative">
              {p.cover && <img src={resolveImg(p.cover)} alt={p.title} className="h-full w-full object-cover" loading="lazy" />}
              {p.featured && <span className="absolute top-2 left-2 rounded-full bg-amber-400 text-amber-950 text-[10px] px-2 py-0.5 font-semibold flex items-center gap-1"><Star className="h-3 w-3" /> Featured</span>}
              {p.badge && <span className="absolute top-2 right-2 rounded-full bg-emerald-500 text-white text-[10px] px-2 py-0.5">{p.badge}</span>}
            </div>
            <div className="p-4 flex items-start justify-between gap-2">
              <div>
                <div className="font-semibold text-sm">{p.title}</div>
                <div className="text-xs text-muted-foreground">{p.days}D · {p.nights}N · ₹{(p.price || 0).toLocaleString("en-IN")}</div>
              </div>
              <div className="flex gap-1">
                <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => { setEditing({ ...EMPTY, ...p }); setIsNew(false); }} data-testid={`edit-pkg-${p.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500" onClick={() => remove(p.id, p.title)} data-testid={`delete-pkg-${p.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="package-editor">
          <DialogHeader><DialogTitle>{isNew ? "Add package" : `Edit — ${editing?.title}`}</DialogTitle></DialogHeader>
          {editing && (
            <div className="grid gap-5">
              <div className="grid gap-2"><Label>Title</Label><Input value={editing.title} onChange={set("title")} data-testid="pkg-field-title" /></div>
              <div className="grid grid-cols-3 gap-4">
                <div className="grid gap-2"><Label>Days</Label><Input type="number" value={editing.days} onChange={set("days")} data-testid="pkg-field-days" /></div>
                <div className="grid gap-2"><Label>Nights</Label><Input type="number" value={editing.nights} onChange={set("nights")} data-testid="pkg-field-nights" /></div>
                <div className="grid gap-2"><Label>Price (₹)</Label><Input type="number" value={editing.price} onChange={set("price")} data-testid="pkg-field-price" /></div>
              </div>
              <ImagePicker label="Cover image" value={editing.cover} onChange={set("cover")} folder="packages" />
              <div className="grid sm:grid-cols-2 gap-4 items-center">
                <div className="grid gap-2"><Label>Badge (optional)</Label><Input value={editing.badge || ""} onChange={set("badge")} placeholder="e.g. Best Seller" data-testid="pkg-field-badge" /></div>
                <label className="flex items-center gap-3 text-sm pt-5">
                  <Switch checked={!!editing.featured} onCheckedChange={set("featured")} data-testid="pkg-field-featured" />
                  Featured (show on home page)
                </label>
              </div>
              <ListEditor label="Destination list (places covered)" items={editing.places} onChange={set("places")} />
              <ListEditor label="Included" items={editing.inclusions} onChange={set("inclusions")} />
              <ListEditor label="Excluded" items={editing.exclusions} onChange={set("exclusions")} />
              <div className="grid gap-2">
                <Label>Itinerary (day-wise)</Label>
                {(editing.itinerary || []).map((it, i) => (
                  <div key={i} className="grid grid-cols-[60px_1fr_auto] gap-2 items-start">
                    <Input type="number" value={it.day} onChange={(e) => set("itinerary")(editing.itinerary.map((x, k) => (k === i ? { ...x, day: Number(e.target.value) } : x)))} />
                    <div className="grid gap-2">
                      <Input value={it.title} placeholder="Day title" onChange={(e) => set("itinerary")(editing.itinerary.map((x, k) => (k === i ? { ...x, title: e.target.value } : x)))} />
                      <Input value={it.desc} placeholder="Description" onChange={(e) => set("itinerary")(editing.itinerary.map((x, k) => (k === i ? { ...x, desc: e.target.value } : x)))} />
                    </div>
                    <Button variant="ghost" size="sm" className="text-red-500" onClick={() => set("itinerary")(editing.itinerary.filter((_, k) => k !== i))}>✕</Button>
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" className="w-fit" onClick={() => set("itinerary")([...(editing.itinerary || []), { day: (editing.itinerary?.length || 0) + 1, title: "", desc: "" }])}>
                  <Plus className="h-3.5 w-3.5 mr-1.5" /> Add day
                </Button>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="save-package-btn">{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
